"use client";

import { useEffect, useSyncExternalStore } from "react";

// Dismissal persists for 24h in localStorage — "comes back on refresh" once
// the 24h TTL expires. Within the window, refresh keeps it hidden.
const STORAGE_KEY = "cv_banner_dismissed_until";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

function readDismissedUntil(): number {
  if (typeof window === "undefined") return 0;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return 0;
  const ts = Number(raw);
  return Number.isFinite(ts) ? ts : 0;
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

function notify() {
  listeners.forEach((l) => l());
}

function getSnapshot(): number {
  return readDismissedUntil();
}

function getServerSnapshot(): number {
  return 0;
}

function getIsDismissedSnapshot(): boolean {
  return readDismissedUntil() > Date.now();
}

function getIsDismissedServerSnapshot(): boolean {
  return false;
}

export function useClaimBannerDismissal() {
  const dismissedUntil = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const dismissed = useSyncExternalStore(
    subscribe,
    getIsDismissedSnapshot,
    getIsDismissedServerSnapshot,
  );

  // If the saved timestamp has passed, clear it lazily so the key doesn't
  // accumulate expired values.
  useEffect(() => {
    if (dismissedUntil && dismissedUntil <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      notify();
    }
  }, [dismissedUntil]);

  return {
    dismissed,
    dismiss: () => {
      const until = Date.now() + DISMISS_DURATION_MS;
      window.localStorage.setItem(STORAGE_KEY, String(until));
      notify();
    },
    reset: () => {
      window.localStorage.removeItem(STORAGE_KEY);
      notify();
    },
  };
}
